import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PlayCircle, Loader2, CheckCircle2, XCircle, Trash2, Zap } from "lucide-react";

const STEP_LABELS = {
  active_campaign: "Active campaign check",
  seed_referrers: "Seed synthetic referrers (with delivered orders)",
  send_invites: "Send WhatsApp invites",
  friend_captures: "Simulate friend captures (claim promo codes)",
  dwh_orders: "Simulate DWH orders",
  process_rewards: "Run reward engine",
  payout_sweep: "Run M-Pesa payout sweep",
};

export default function SmokeRunner() {
  const [form, setForm] = useState({ seed_referrers: 3, friends_per_referrer: 2 });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [cleaning, setCleaning] = useState(false);
  const [toast, setToast] = useState("");

  const run = async () => {
    setRunning(true);
    setErr("");
    setResult(null);
    try {
      const { data } = await api.post("/smoke/run-full", form);
      setResult(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Smoke run failed");
    } finally {
      setRunning(false);
    }
  };

  const cleanup = async () => {
    if (!window.confirm("Delete all smoke-tagged referrers, links, referrals and DWH orders?")) return;
    setCleaning(true);
    try {
      const { data } = await api.post("/smoke/cleanup");
      const total = Object.values(data.removed || {}).reduce((a, b) => a + b, 0);
      setToast(`Cleaned ${total} records (${Object.entries(data.removed).map(([k, v]) => `${k}:${v}`).join(" · ")})`);
      setTimeout(() => setToast(""), 5000);
      setResult(null);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Cleanup failed");
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-[#1a1c19] sm:text-3xl flex items-center gap-2">
          <Zap className="h-6 w-6 text-[#ff3399]" /> Smoke Test Runner
        </h1>
        <p className="text-sm text-[#5c665a]">
          One-click replay of the full Refer &amp; Earn lifecycle — seed referrers, send invites, simulate captures, orders, rewards and payouts. Every synthetic record is tagged <code className="rounded bg-[#fafcfa] px-1 text-[11px] font-mono">smoke:true</code> so you can clean up after.
        </p>
      </div>

      {toast && <div className="rounded-lg bg-[#eaf8de] px-4 py-2 text-sm font-semibold text-[#3f7c21]" data-testid="smoke-toast">{toast}</div>}

      <div className="admin-card">
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#5c665a]">Scenario parameters</h3>
        <div className="mt-3 grid gap-4 sm:grid-cols-3">
          <label className="block">
            <span className="text-xs font-semibold text-[#5c665a]">Referrers to seed</span>
            <input
              type="number" min="1" max="10"
              value={form.seed_referrers}
              onChange={(e) => setForm((f) => ({ ...f, seed_referrers: Number(e.target.value) }))}
              className="admin-input mt-1 w-full"
              data-testid="smoke-seed-referrers"
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-[#5c665a]">Friends per referrer</span>
            <input
              type="number" min="1" max="10"
              value={form.friends_per_referrer}
              onChange={(e) => setForm((f) => ({ ...f, friends_per_referrer: Number(e.target.value) }))}
              className="admin-input mt-1 w-full"
              data-testid="smoke-friends-per"
            />
          </label>
          <div className="flex items-end gap-2">
            <button
              className="admin-btn flex-1"
              onClick={run}
              disabled={running}
              data-testid="smoke-run-btn"
            >
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
              {running ? "Running…" : "Run full cycle"}
            </button>
          </div>
        </div>
      </div>

      {err && (
        <div className="rounded-lg bg-[#fff0f5] px-4 py-3 text-sm font-semibold text-[#c01974]" data-testid="smoke-error">
          {err}
        </div>
      )}

      {result && (
        <div className="admin-card" data-testid="smoke-results">
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {Object.entries(result.summary).map(([k, v]) => (
              <div key={k} className="rounded-lg bg-[#fafcfa] px-3 py-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#5c665a]">{k.replace(/_/g, " ")}</div>
                <div className="text-2xl font-extrabold text-[#1a1c19]">{v}</div>
              </div>
            ))}
          </div>

          <div className="mt-5">
            <h3 className="text-sm font-bold uppercase tracking-wider text-[#5c665a]">Timeline</h3>
            <ol className="mt-3 space-y-2">
              {result.timeline.map((t, i) => (
                <li key={i} className="flex items-start gap-3 rounded-lg border border-[#e2e8e2] bg-white px-3 py-2" data-testid={`smoke-step-${t.step}`}>
                  <div className="mt-0.5">
                    {t.ok === false
                      ? <XCircle className="h-5 w-5 text-red-500" />
                      : <CheckCircle2 className="h-5 w-5 text-[#66cc33]" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-bold text-[#1a1c19]">
                        {i + 1}. {STEP_LABELS[t.step] || t.step}
                      </div>
                      <div className="text-[10px] text-[#5c665a]">{new Date(t.at).toLocaleTimeString()}</div>
                    </div>
                    <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all text-[11px] text-[#5c665a]">
                      {JSON.stringify(Object.fromEntries(Object.entries(t).filter(([k]) => !["step", "at", "ok"].includes(k))), null, 0)}
                    </pre>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}

      <div className="admin-card">
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#5c665a]">Cleanup</h3>
        <p className="mt-1 text-xs text-[#5c665a]">
          Remove every record tagged <code className="rounded bg-[#fafcfa] px-1 text-[11px] font-mono">smoke:true</code> across referrers, links, referrals and DWH orders. Safe — it won't touch your real data or seed referrers.
        </p>
        <button
          className="admin-btn-danger mt-3"
          onClick={cleanup}
          disabled={cleaning}
          data-testid="smoke-cleanup-btn"
        >
          {cleaning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          Clean up smoke data
        </button>
      </div>
    </div>
  );
}
